import React, { memo } from 'react'
import { cn, Checkbox } from '@yanolja-rnd/nol-design-system'
import { DomesticCartBusOption } from '~/api/converters/domestic/cart/ProductSection/Bus/index.type'
import ProductThumbnail from '~/app/components-nds/common/thumbnail'
import { getDisabledClasses } from '../utils'
import useDomesticCartStore from '~/app/store/domestic/cart/cart'
import { useDomesticCartQuery } from '~/app/react-query/domestic/cart'
import CancellationPolicy from '~/app/components-nds/common/cancellation-policy'
import ProductOutdated from '~/app/components-nds/common/product-outdated'
import OptionDeleteButton from '~/app/components-nds/domestic/cart/cart-product-section/items/common/option-delete-button'

interface BusOptionProps {
  /** 버스 옵션 데이터 */
  option: DomesticCartBusOption
  /** 선택 가능 여부 */
  isSelectable: boolean
  /** 제목 클릭 핸들러 */
  onTitleClick: (deepLinkUrl: string) => void
}

interface BusDetailInfoRowProps {
  /** 라벨 텍스트 */
  label: string
  /** 표시할 내용 */
  infoTexts: string[]
  /** 불가 여부 */
  disabled: boolean
}
/**
 * 버스 옵션 정보 행 컴포넌트
 */
const BusDetailInfoRow: React.FC<BusDetailInfoRowProps> = ({ label, infoTexts, disabled }) => {
  return (
    <>
      <div className="mt-1 flex h-fit min-h-20 rounded-6 bg-static-white px-6 pt-2">
        <span className="whitespace-nowrap typography-caption-11-bold">{label}</span>
      </div>
      <div
        className={getDisabledClasses(disabled, 'grid gap-4 typography-body-14-regular-multiline')}
      >
        {infoTexts?.map((info, index) => (
          <div key={index}>{info}</div>
        ))}
      </div>
    </>
  )
}

/**
 * 버스 옵션 컴포넌트
 */
const BusOption: React.FC<BusOptionProps> = ({ option, isSelectable, onTitleClick }) => {
  const {
    optionId,
    optionTitle,
    imageUrl,
    departureDate,
    priceText,
    busGradeSeatInfo,
    priceQuantityInfo,
    isSaleAvailable,
    disabledResonText,
    deepLinkUrl,
    cancellationPolicy,
    needUpdate,
  } = option

  // 장바구니 상태 관리
  const { isSelected, selectOption, unselectOption } = useDomesticCartStore(
    ({ selectedOptionIds, selectOption, unselectOption }) => {
      return {
        isSelected: selectedOptionIds.has(optionId),
        selectOption,
        unselectOption,
      }
    }
  )

  const { refetch } = useDomesticCartQuery()

  // 체크박스 변경 핸들러
  const handleCheckboxChange = () => {
    if (isSelected) {
      unselectOption(optionId)
    } else {
      selectOption(optionId)
    }
  }

  return (
    <div className="flex flex-col gap-16 bg-static-white" role="listitem">
      {/* 헤더 영역 - 체크박스와 삭제 버튼 */}
      <div className="flex items-start gap-8">
        <div className="flex w-full items-start gap-6">
          {isSelectable && (
            <Checkbox
              size="small"
              checked={isSelected}
              disabled={!isSaleAvailable}
              onChange={handleCheckboxChange}
            />
          )}
          <div
            className={getDisabledClasses(
              !isSaleAvailable,
              'cursor-pointer typography-subtitle-16-bold'
            )}
            onClick={() => onTitleClick?.(deepLinkUrl)}
          >
            {optionTitle}
          </div>
        </div>

        {isSelectable && (
          <OptionDeleteButton optionId={optionId} aria-label={`${optionTitle} 삭제`} />
        )}
      </div>

      {/* 메인 정보 영역 - 썸네일과 기본 정보 */}
      <div className="flex items-start gap-12">
        <ProductThumbnail
          image={imageUrl}
          isDisabled={!isSaleAvailable}
          className={cn('h-118 min-w-88', 'pc:h-144 pc:min-w-108')}
          alt={`${optionTitle} 버스 이미지`}
        />
        <div className={cn('flex min-h-118 w-full flex-col justify-between', 'pc:min-h-144')}>
          {/* 출발일 정보 */}
          <div
            className={getDisabledClasses(
              !isSaleAvailable,
              'break-all typography-body-14-regular-multiline'
            )}
          >
            {departureDate}
          </div>

          {/* 가격 정보 */}
          <div className="mt-12 flex flex-col items-end gap-4">
            <div
              className={getDisabledClasses(
                !isSaleAvailable,
                'break-all text-right typography-subtitle-16-bold',
                'line-through'
              )}
            >
              {priceText}
            </div>
            {!isSaleAvailable && <div className="typography-body-12-bold">{disabledResonText}</div>}
          </div>
        </div>
      </div>

      {/* 상세 예약 정보 */}
      <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-8 rounded-12 bg-fill-neutral-weak p-16">
        {/* 버스/등급/좌석 정보 */}
        <BusDetailInfoRow label="선택" infoTexts={[busGradeSeatInfo]} disabled={!isSaleAvailable} />

        {/* 인원 정보 */}
        <BusDetailInfoRow label="인원" infoTexts={priceQuantityInfo} disabled={!isSaleAvailable} />
      </div>
      {/* 취소 규정 영역 */}
      {!!cancellationPolicy &&
        (cancellationPolicy.type === 'policy-error' ? (
          <CancellationPolicy {...cancellationPolicy} onRefresh={() => refetch()} />
        ) : (
          <CancellationPolicy {...cancellationPolicy} />
        ))}
      {/* 상품 정보 변경 영역 */}
      {needUpdate && <ProductOutdated />}
    </div>
  )
}

// React.memo를 사용하여 props가 변경되지 않으면 리렌더링 방지
export default memo(BusOption)
